"use client";

import * as React from "react";
import { QuizSessionBoundary } from "@/app/my/personal-quiz/QuizSessionProvider";
import { usePathname, useSelectedLayoutSegment } from "next/navigation";
import ContentWrapper from "@/components/ContentWrapper";
import ArticleShell from "@/components/article-shell/ArticleShell";
import IndexShell from "@/components/article-shell/IndexShell";
import ForumProblemShell from "@/components/forum-shell/ForumProblemShell";
import TestCenterShell from "@/components/test-center-shell/TestCenterShell";
import AdminProblemShell from "@/components/article-shell/AdminProblemShell";
import RailShell from "@/components/article-shell/RailShell";
import { SubjectIndexMobileToolsProvider } from "@/components/SubjectIndexMobileTools";
import { MobileBottomToolProvider } from "@/components/mobile-bottom-tools";
import { useIsDesktopShell } from "@/hooks/useViewport";
import { randomProblemsSessionRegistry } from "@/lib/random-problems-session";
import {
  isAdminProblemPreviewPage,
  isShellPage,
  isCustomShellPage,
  isSubjectIndexPage,
  isCardIndexShellPage,
  isCommunityShellPage,
  isForumProblemPage,
  isTestCenterPage,
  NOT_FOUND_SHELL_LABEL,
} from "@/lib/route-layout";

/** 매칭되는 라우트가 없는 URL 에서 Next 가 루트 children 에 채우는 세그먼트 (next/dist/shared/lib/segment). */
const NEXT_NOT_FOUND_SEGMENT = "/_not-found";

/**
 * 레이아웃 분기 (JCG-2060, JCG-2066, JCG-2097, JCG-2106).
 * 데스크탑(≥1024px) + 셸 대상 페이지(이론/코딩 글 + 기출·모의고사 문제 풀이) → 3컬럼 셸(상단 NAV 없음).
 * 데스크탑 + 인덱스 페이지(`/theory`) → IndexShell(카드형 3컬럼, 상단 NAV 없음, JCG-2097).
 * 데스크탑 + 시험 후기 담벼락(`/community`) → 대시보드 셸(CommunityClient 가 그리드 소유, JCG-2106).
 * 데스크탑 + 그 외 모든 경로(404 포함) → RailShell(아이콘 레일 + 상단 제목 바, JCG-3679). 데스크탑 클래식 없음.
 * 모바일/태블릿(<1024px) → 클래식 레이아웃(상단 Navbar + TrialBanner + 단일 컬럼 본문).
 *
 * 서버 컴포넌트(Navbar/AdSenseWrapper/Footer 등)는 LayoutContent 에서 렌더해 element prop 으로
 * 전달받는다 → 프로바이더 중복·더블 크롬 없음.
 */
interface LayoutSwitchProps {
  children: React.ReactNode;
  navbar: React.ReactNode;
  trialBanner: React.ReactNode;
  leftAd: React.ReactNode;
  rightAd: React.ReactNode;
  footer: React.ReactNode;
  globals: React.ReactNode;
}

export default function LayoutSwitch(props: LayoutSwitchProps) {
  const pathname = usePathname();
  const layout = <ResponsiveLayoutSwitch {...props} />;
  return pathname === "/my/personal-quiz"
    ? <QuizSessionBoundary>{layout}</QuizSessionBoundary>
    : layout;
}

function ResponsiveLayoutSwitch({
  children,
  navbar,
  trialBanner,
  leftAd,
  rightAd,
  footer,
  globals,
}: LayoutSwitchProps) {
  const pathname = usePathname() ?? "";
  // 셸 게이트 전용 훅(서버 스냅샷 true) — CommunityClient 와 동일 판정 공유(@/hooks/useViewport).
  const isDesktop = useIsDesktopShell();
  // 매칭 라우트가 없는 URL(404) — RailShell 상단바 제목에만 쓴다(JCG-3679). SSR 에서도 판정된다.
  const isNotFound = useSelectedLayoutSegment() === NEXT_NOT_FOUND_SEGMENT;

  // RandomProblems 자체의 언마운트보다 확정된 라우트 commit을 기준으로 SPA 이탈을
  // 통지한다(JCG-3044). Next가 이전 page 트리를 보존해도 재진입 시 새 세트가 출제된다.
  React.useLayoutEffect(() => {
    randomProblemsSessionRegistry.commitPathname(pathname);
  }, [pathname]);

  if (isDesktop && isShellPage(pathname)) {
    return (
      <ArticleShell
        leftAd={leftAd}
        rightAd={rightAd}
        footer={footer}
        globals={globals}
        variant={isCustomShellPage(pathname) ? "custom" : "article"}
      >
        {children}
      </ArticleShell>
    );
  }

  // 카드형 인덱스 페이지(`/theory`, `/coding`, `/summary-pdf`, `/past-exam` 루트+연도 인덱스 7개)
  // — 데스크탑에서 전용 IndexShell(카드형 3컬럼 셸). 광고는 미노출(isSidebarAdHidden)이라 사이드바
  // 슬롯은 카드 전용. 좌/우 카드 분배는 페이지가 IndexShell 슬롯에 portal (JCG-2097/2099, summary-pdf
  // 카탈로그는 JCG-2120, past-exam 회차 목록은 JCG-2164 PastExamIndexShell). 인덱스 셸 custom(/forum/jcg)은 위 isShellPage 분기.
  if (isDesktop && isCardIndexShellPage(pathname)) {
    return (
      <IndexShell footer={footer} globals={globals}>
        {children}
      </IndexShell>
    );
  }

  // 시험 후기 담벼락(/community) — 데스크탑 3컬럼 대시보드 셸 (JCG-2106).
  // 셸 chrome(IconRail·슬림 헤더·좌/우 사이드바 그리드)은 CommunityClient(children)가 직접 소유한다
  // (히어로·피드가 공유하는 reviews state 가 거기 있어 그리드를 그쪽에서 그려야 함). 상단 NAV 는 미렌더,
  // 광고도 미노출(좌 후보투표·우 후기피드가 사이드바를 채움). 여기선 footer·globals 만 그리드 뒤에 둔다.
  if (isDesktop && isCommunityShellPage(pathname)) {
    return (
      <>
        {children}
        {footer}
        {globals}
      </>
    );
  }

  // 문제 포럼 상세(noindex 내부 도구, JCG-2105) — ArticleShell 대신 경량 셸.
  // 좌: 같은 scope 관련 문제 네비 / 중앙: 문제 + 토론 / 우: 광고 또는 풀이 변신 패널.
  if (isDesktop && isForumProblemPage(pathname)) {
    return (
      <ForumProblemShell footer={footer} globals={globals}>
        {children}
      </ForumProblemShell>
    );
  }

  // 시험장 찾기 전용 셸(JCG-2100) — 콘텐츠 구성이 달라 ArticleShell 과 분리.
  if (isDesktop && isTestCenterPage(pathname)) {
    return (
      <TestCenterShell footer={footer} globals={globals}>
        {children}
      </TestCenterShell>
    );
  }

  if (isDesktop && !isNotFound && isAdminProblemPreviewPage(pathname)) {
    return (
      <React.Suspense fallback={<RailShell footer={footer} globals={globals}>{children}</RailShell>}>
        <AdminProblemShell footer={footer} globals={globals}>
          {children}
        </AdminProblemShell>
      </React.Suspense>
    );
  }

  // 데스크탑 기본 셸 = 아이콘 레일 전용 경량 셸(JCG-2111/2122/2165, JCG-3679).
  // 위 전용 셸 게이트에 걸리지 않은 데스크탑 경로는 전부 RailShell 로 렌더한다 — 데스크탑 클래식 레이아웃
  // (상단 NAV·trialBanner·좌/우 광고 레일)은 없다. 전역 아이콘 레일만 두고 좌/우 사이드바·광고 없이 본문 폭을
  // 페이지에 맡긴다(가격 카드·정적 문서·Clerk 위젯·관리자 표). 상단바 제목은 404 면 NOT_FOUND_SHELL_LABEL,
  // 아니면 route 라벨(getRailOnlyShellLabel, 미등록 경로는 빈 제목). 셸 경로 안의 404 는 해당 셸을 그대로 쓴다.
  if (isDesktop) {
    return (
      <RailShell
        footer={footer}
        globals={globals}
        title={isNotFound ? NOT_FOUND_SHELL_LABEL : undefined}
      >
        {children}
      </RailShell>
    );
  }

  // 모바일/태블릿(<1024px) 클래식 레이아웃 — 상단 Navbar + TrialBanner + 단일 컬럼 본문.
  // 사이드바 광고 레일(lg 이상 노출)은 데스크탑이 전부 셸로 빠지며 쓰이지 않게 되어 제거했다(JCG-3679).
  const content = isSubjectIndexPage(pathname) ? (
    <SubjectIndexMobileToolsProvider>
      {children}
      {footer}
      {globals}
    </SubjectIndexMobileToolsProvider>
  ) : (
    <>
      {children}
      {footer}
      {globals}
    </>
  );

  return (
    <>
      {navbar}
      {trialBanner}
      <div className="flex justify-center md:px-4">
        <ContentWrapper>
          <MobileBottomToolProvider dockLabel="모바일 하단 도구">
            {content}
          </MobileBottomToolProvider>
        </ContentWrapper>
      </div>
    </>
  );
}
